import React from 'react';
import { 
  Calendar, Download, Filter, MoreVertical, ArrowDownLeft, TrendingUp, TrendingDown 
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart as RePieChart, Pie, Cell 
} from 'recharts';
import { cn } from '../lib/utils';

type AccountAmounts = Record<string, number>;

interface LocalProfitLossReport {
  totalIncome: number;
  totalExpenses: number;
  totalDirectCosts: number;
  grossProfit: number;
  netProfit: number;
  incomeByAccount: AccountAmounts;
  directCostByAccount: AccountAmounts;
  expenseByAccount: AccountAmounts;
  expenseBreakdown: { name: string; value: number }[];
  comparison?: { totalIncome: number; totalExpenses: number; netProfit: number };
}

const toAmount = (value: unknown): number => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const normalizeAccounts = (value: unknown, defaultName: string): AccountAmounts => {
  if (typeof value === 'number' || typeof value === 'string') {
    return { [defaultName]: toAmount(value) };
  }

  if (Array.isArray(value)) {
    return value.reduce<AccountAmounts>((accounts, entry, index) => {
      if (entry && typeof entry === 'object') {
        const item = entry as Record<string, unknown>;
        const name = String(item.name ?? item.accountName ?? item.account ?? `Account ${index + 1}`);
        accounts[name] = toAmount(item.amount ?? item.value ?? item.balance);
      }
      return accounts;
    }, {});
  }

  if (value && typeof value === 'object') {
    const reportValue = value as Record<string, unknown>;
    const total = reportValue.total ?? reportValue.amount ?? reportValue.value;
    const entries = reportValue.accounts ?? reportValue.items ?? reportValue.breakdown;

    if (entries !== undefined) {
      return normalizeAccounts(entries, defaultName);
    }

    if (total !== undefined) {
      return { [defaultName]: toAmount(total) };
    }

    return Object.entries(reportValue).reduce<AccountAmounts>((accounts, [name, amount]) => {
      if (typeof amount === 'number' || typeof amount === 'string') {
        accounts[name] = toAmount(amount);
      }
      return accounts;
    }, {});
  }

  return {};
};

const totalFor = (value: unknown, accounts: AccountAmounts): number => {
  if (typeof value === 'number' || typeof value === 'string') {
    return toAmount(value);
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const reportValue = value as Record<string, unknown>;
    const total = reportValue.total ?? reportValue.amount ?? reportValue.value;
    if (total !== undefined) return toAmount(total);
  }

  return Object.values(accounts).reduce((total, amount) => total + amount, 0);
};

const comparisonRange = (startDate: string, endDate: string, compareWith: string) => {
  if (compareWith === 'None') return null;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const months = compareWith.includes('year') ? 12 * Number.parseInt(compareWith, 10) : compareWith.includes('month') ? Number.parseInt(compareWith, 10) : 0;
  if (!months) return null;
  const from = new Date(start); from.setMonth(from.getMonth() - months);
  const to = new Date(end); to.setMonth(to.getMonth() - months);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

const normalizeLocalReport = (response: Record<string, unknown>): LocalProfitLossReport => {
  const incomeByAccount = normalizeAccounts(response.income, 'Income');
  const expenseByAccount = normalizeAccounts(response.expenses, 'Expenses');
  const totalIncome = totalFor(response.income, incomeByAccount);
  const totalExpenses = totalFor(response.expenses, expenseByAccount);
  const netIncome = response.netIncome ?? response.net_income;
  const netProfit = netIncome === undefined ? totalIncome - totalExpenses : toAmount(netIncome);

  const comparisonValue = response.comparison && typeof response.comparison === 'object' ? response.comparison as Record<string, unknown> : null;
  const comparisonIncome = comparisonValue ? normalizeAccounts(comparisonValue.income, 'Income') : {};
  const comparisonExpenses = comparisonValue ? normalizeAccounts(comparisonValue.expenses, 'Expenses') : {};
  return {
    totalIncome,
    totalExpenses,
    totalDirectCosts: 0,
    grossProfit: totalIncome,
    netProfit,
    incomeByAccount,
    directCostByAccount: {},
    expenseByAccount,
    expenseBreakdown: Object.entries(expenseByAccount).map(([name, value]) => ({ name, value })),
    comparison: comparisonValue ? {
      totalIncome: totalFor(comparisonValue.income, comparisonIncome),
      totalExpenses: totalFor(comparisonValue.expenses, comparisonExpenses),
      netProfit: toAmount(comparisonValue.netIncome ?? comparisonValue.net_income) || totalFor(comparisonValue.income, comparisonIncome) - totalFor(comparisonValue.expenses, comparisonExpenses)
    } : undefined
  };
};

interface ProfitLossReportProps {
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  compareWith: string;
  setCompareWith: (val: string) => void;
  basis: 'Accrual' | 'Cash';
  setBasis: (basis: 'Accrual' | 'Cash') => void;
  trackingCategories: any[];
  showAccountCodes: boolean;
  setShowAccountCodes: (val: boolean) => void;
  showDecimals: boolean;
  setShowDecimals: (val: boolean) => void;
  isFilterMenuOpen: boolean;
  setIsFilterMenuOpen: (val: boolean) => void;
  isMoreMenuOpen: boolean;
  setIsMoreMenuOpen: (val: boolean) => void;
  handleExport: () => void;
  totalIncome: number;
  totalExpenses: number;
  totalDirectCosts: number;
  grossProfit: number;
  netProfit: number;
  incomeByAccount: Record<string, number>;
  directCostByAccount: Record<string, number>;
  expenseByAccount: Record<string, number>;
  expenseBreakdown: any[];
  COLORS: string[];
  setActiveReport: (report: string | null) => void;
}

export const ProfitLossReport = ({
  startDate, endDate, setStartDate, setEndDate,
  compareWith, setCompareWith, basis, setBasis,
  trackingCategories, showAccountCodes, setShowAccountCodes,
  showDecimals, setShowDecimals, isFilterMenuOpen, setIsFilterMenuOpen,
  isMoreMenuOpen, setIsMoreMenuOpen, handleExport,
  totalIncome: firebaseTotalIncome, totalExpenses: firebaseTotalExpenses,
  totalDirectCosts: firebaseTotalDirectCosts, grossProfit: firebaseGrossProfit, netProfit: firebaseNetProfit,
  incomeByAccount: firebaseIncomeByAccount, directCostByAccount: firebaseDirectCostByAccount,
  expenseByAccount: firebaseExpenseByAccount, expenseBreakdown: firebaseExpenseBreakdown,
  COLORS, setActiveReport
}: ProfitLossReportProps) => {
  const [localReport, setLocalReport] = React.useState<LocalProfitLossReport | null>(null);

  React.useEffect(() => {
    const token = localStorage.getItem('app_auth_token');
    const organizationId = localStorage.getItem('selected_org_id');

    if (!token || !organizationId) {
      setLocalReport(null);
      return;
    }

    const controller = new AbortController();
    const query = new URLSearchParams();
    if (startDate) query.set('startDate', startDate);
    if (endDate) query.set('endDate', endDate);
    const comparison = comparisonRange(startDate, endDate, compareWith);
    if (comparison) {
      query.set('compareFrom', comparison.from);
      query.set('compareTo', comparison.to);
    }
    const queryString = query.toString();

    fetch(`/api/organizations/${encodeURIComponent(organizationId)}/reports/profit-loss${queryString ? `?${queryString}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Profit and loss request failed: ${response.status}`);
        return response.json() as Promise<Record<string, unknown>>;
      })
      .then((response) => {
        const report = response.data && typeof response.data === 'object'
          ? response.data as Record<string, unknown>
          : response;
        setLocalReport(normalizeLocalReport(report));
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setLocalReport(null);
        }
      });

    return () => controller.abort();
  }, [startDate, endDate, compareWith]);

  const report = localReport ?? {
    totalIncome: firebaseTotalIncome,
    totalExpenses: firebaseTotalExpenses,
    totalDirectCosts: firebaseTotalDirectCosts,
    grossProfit: firebaseGrossProfit,
    netProfit: firebaseNetProfit,
    incomeByAccount: firebaseIncomeByAccount,
    directCostByAccount: firebaseDirectCostByAccount,
    expenseByAccount: firebaseExpenseByAccount,
    expenseBreakdown: firebaseExpenseBreakdown,
    comparison: undefined
  };

  const {
    totalIncome, totalExpenses, totalDirectCosts, grossProfit, netProfit,
    incomeByAccount, directCostByAccount, expenseByAccount, expenseBreakdown
  } = report;
  const comparison = report.comparison;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date range:</span>
            <select 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value={new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]}>This month</option>
              <option value={new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0]}>Last month</option>
              <option value={new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1).toISOString().split('T')[0]}>This quarter</option>
              <option value={new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]}>This financial year</option>
            </select>
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Compare with</span>
            <select 
              value={compareWith}
              onChange={(e) => setCompareWith(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="None">None</option>
              <option value="1 month">1 month</option>
              <option value="2 months">2 months</option>
              <option value="3 months">3 months</option>
              <option value="1 year">1 year</option>
            </select>
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Compare tracking categories</span>
            <select 
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value="None">None</option>
              {trackingCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <button 
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
          >
            <Filter className="w-4 h-4" />
            Filter
          </button>

          <div className="relative">
            <button 
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
            >
              <MoreVertical className="w-4 h-4" />
              More
            </button>
            
            {isMoreMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl shadow-xl z-50 p-4 space-y-4">
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Accounting basis</p>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setBasis('Accrual')}
                      className={cn("flex-1 py-1 text-[10px] font-bold uppercase rounded-lg border", basis === 'Accrual' ? "bg-[#141414] text-white border-[#141414]" : "border-[#141414]/20")}
                    >
                      Accrual
                    </button>
                    <button 
                      onClick={() => setBasis('Cash')}
                      className={cn("flex-1 py-1 text-[10px] font-bold uppercase rounded-lg border", basis === 'Cash' ? "bg-[#141414] text-white border-[#141414]" : "border-[#141414]/20")}
                    >
                      Cash
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Show</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showAccountCodes} onChange={(e) => setShowAccountCodes(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Account codes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showDecimals} onChange={(e) => setShowDecimals(e.target.checked)} className="rounded border-[#141414]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Decimals</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={handleExport}
            className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Generate Report
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Profit and Loss</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            Demo Company (Global)
          </p>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
            For the month ended {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          {comparison && <p className="text-xs font-bold opacity-50 mt-2">Compared with {compareWith}: net profit {comparison.netProfit >= 0 ? '+' : '-'}${Math.abs(comparison.netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <div className="h-[400px] relative">
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 mb-6">Income vs Expenses</h3>
            <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1} debounce={50}>
              <BarChart data={[{
                name: 'Current', income: totalIncome, expenses: totalExpenses
              }, ...(comparison ? [{ name: 'Comparison', income: comparison.totalIncome, expenses: comparison.totalExpenses }] : [])]}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#14141410" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 600}} />
                <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 600}} />
                <Tooltip 
                  contentStyle={{borderRadius: '16px', border: '1px solid #141414', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}}
                />
                <Bar dataKey="income" fill="#141414" radius={[4, 4, 0, 0]} name="Total Income" />
                <Bar dataKey="expenses" fill="var(--primary)" radius={[4, 4, 0, 0]} name="Total Expenses" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="h-[400px] relative">
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 mb-6">Expense Breakdown</h3>
            {expenseBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1} debounce={50}>
                <RePieChart>
                  <Pie
                    data={expenseBreakdown}
                    innerRadius={80}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {expenseBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center opacity-30 italic serif">
                No expenses recorded for this period.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-8 max-w-3xl mx-auto">
          <section className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Trading Income</h3>
            {Object.entries(incomeByAccount).map(([name, amount]) => (
              <div key={name} className="flex justify-between items-center py-2 border-t border-[#141414]/5 first:border-t-0">
                <span className="font-bold">{name}</span>
                <span className="font-mono font-bold">${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
            {Object.keys(incomeByAccount).length === 0 && (
              <p className="text-xs opacity-50 italic serif py-2">No income recorded.</p>
            )}
            <div className="flex justify-between items-center py-4 border-t-2 border-[#141414] bg-[#141414]/5 px-4 rounded-xl mt-4">
              <span className="text-lg font-bold italic serif">Total Income</span>
              <span className="text-lg font-mono font-bold">${totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Less Cost of Sales</h3>
            {Object.entries(directCostByAccount).map(([name, amount]) => (
              <div key={name} className="flex justify-between items-center py-2 border-t border-[#141414]/5 first:border-t-0">
                <span className="font-bold">{name}</span>
                <span className="font-mono font-bold">${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
            {Object.keys(directCostByAccount).length === 0 && (
              <p className="text-xs opacity-50 italic serif py-2">No direct costs recorded.</p>
            )}
            <div className="flex justify-between items-center py-4 border-t-2 border-[#141414] bg-[#141414]/5 px-4 rounded-xl mt-4">
              <span className="text-lg font-bold italic serif">Total Cost of Sales</span>
              <span className="text-lg font-mono font-bold">${totalDirectCosts.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </section>

          <section className="py-4">
            <div className="flex justify-between items-center py-4 border-y-2 border-[#141414] px-4">
              <span className="text-xl font-bold italic serif">Gross Profit</span>
              <span className="text-xl font-mono font-bold">${grossProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Operating Expenses</h3>
            {Object.entries(expenseByAccount).map(([name, amount]) => (
              <div key={name} className="flex justify-between items-center py-2 border-t border-[#141414]/5 first:border-t-0">
                <span className="font-bold">{name}</span>
                <span className="font-mono font-bold">${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
            {Object.keys(expenseByAccount).length === 0 && (
              <p className="text-xs opacity-50 italic serif py-2">No expenses recorded.</p>
            )}
            <div className="flex justify-between items-center py-4 border-t-2 border-[#141414] bg-[#141414]/5 px-4 rounded-xl mt-4">
              <span className="text-lg font-bold italic serif">Total Expenses</span>
              <span className="text-lg font-mono font-bold">${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </section>

          <section className="pt-8">
            <div className={cn(
              "flex justify-between items-center p-8 rounded-[32px] shadow-xl",
              netProfit >= 0 ? "bg-[#141414] text-[#E4E3E0]" : "bg-red-900 text-white"
            )}>
              <div>
                <h3 className="text-sm uppercase tracking-widest font-bold opacity-50">Net Profit</h3>
                <p className="text-4xl font-bold tracking-tighter italic serif mt-2">
                  {netProfit >= 0 ? '+' : '-'}${Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
              {netProfit >= 0 ? (
                <TrendingUp className="w-12 h-12 text-green-400" />
              ) : (
                <TrendingDown className="w-12 h-12 text-red-400" />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
